import type { RatingField as RatingFieldSchema } from "shared"
import styles from "./RatingField.module.scss"

type Props = {
  field: RatingFieldSchema
  value: number | undefined
  onChange: (value: number) => void
  disabled?: boolean
}

// The accessible name for a point is its `ariaLabel` (e.g. "Disagree"); the visible
// digit is a shorthand only. Falls back to the visible label for scales that carry
// no ariaLabel.
const nameOf = (opt: RatingFieldSchema["options"][number]) =>
  opt.ariaLabel ?? opt.label

export function RatingField({ field, value, onChange, disabled }: Props) {
  const { options } = field
  // The visible end labels of the scale are the first and last points' meaning.
  const low = options[0]
  const high = options[options.length - 1]

  return (
    <fieldset
      className={styles.fieldset}
      aria-describedby={`${field.id}-scale`}
      tabIndex={-1}
    >
      <legend className={styles.legend}>{field.label}</legend>
      <div id={`${field.id}-scale`} className={styles.ratingsHeader}>
        <span className={styles.endLabel}>{low && nameOf(low)}</span>
        <span className={styles.endLabel}>{high && nameOf(high)}</span>
      </div>
      <div className={styles.ratings}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`${styles.ratingLabel} ${
              value === opt.value ? styles.ratingLabelSelected : ""
            }`}
          >
            <input
              type="radio"
              name={field.id}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              disabled={disabled}
              className={styles.ratingInput}
              // The visible number is decorative; announce the scale meaning.
              aria-label={nameOf(opt)}
              tabIndex={
                value === opt.value ||
                (value === undefined && opt === options[0])
                  ? 0
                  : -1
              }
            />
            <span className={styles.radioCircle} />
            <span className={styles.ratingNumber} aria-hidden="true">
              {opt.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
